/**
 * 2D vector math on the ground plane (XZ in the 3D world maps to xy here).
 * The arena is top-down, so all gameplay movement happens on a flat plane;
 * keeping the sim in 2D keeps it cheap and deterministic.
 *
 * Plain `{ x, y }` objects are used so they serialize directly into snapshots.
 */
export interface Vec2 {
  x: number;
  y: number;
}

export const vec2 = (x = 0, y = 0): Vec2 => ({ x, y });

export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });

export const lengthSq = (a: Vec2): number => a.x * a.x + a.y * a.y;
export const length = (a: Vec2): number => Math.sqrt(lengthSq(a));

/** Returns a unit-length copy, or {0,0} for a zero vector (avoids NaN). */
export const normalize = (a: Vec2): Vec2 => {
  const len = length(a);
  return len > 1e-6 ? { x: a.x / len, y: a.y / len } : { x: 0, y: 0 };
};

/** Clamps a vector's magnitude to `max` (used to cap input/move speed). */
export const clampLength = (a: Vec2, max: number): Vec2 => {
  const len = length(a);
  return len > max ? scale(a, max / len) : { x: a.x, y: a.y };
};

export const distance = (a: Vec2, b: Vec2): number => length(sub(a, b));

/** Unit direction from `from` to `to` (or {0,0} if coincident). */
export const directionTo = (from: Vec2, to: Vec2): Vec2 => normalize(sub(to, from));

/** Rotate a vector by `radians` (CCW in xy). Used for deterministic aim jitter. */
export const rotate = (v: Vec2, radians: number): Vec2 => {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
};

/** Axis-aligned bounding box on the ground plane (used for static cover). */
export interface Aabb {
  min: Vec2;
  max: Vec2;
}

export const pointVsAabb = (p: Vec2, box: Aabb): boolean =>
  p.x >= box.min.x && p.x <= box.max.x && p.y >= box.min.y && p.y <= box.max.y;

export const circleVsCircle = (a: Vec2, ra: number, b: Vec2, rb: number): boolean => {
  const r = ra + rb;
  return lengthSq(sub(a, b)) <= r * r;
};

export const circleVsAabb = (c: Vec2, r: number, box: Aabb): boolean => {
  const nx = Math.max(box.min.x, Math.min(c.x, box.max.x));
  const ny = Math.max(box.min.y, Math.min(c.y, box.max.y));
  const dx = c.x - nx;
  const dy = c.y - ny;
  return dx * dx + dy * dy <= r * r;
};

/**
 * Push a circle out of an AABB along the shortest axis. Returns the corrected
 * center (unchanged if there is no overlap). Used for character-vs-cover.
 */
export const resolveCircleAabb = (c: Vec2, r: number, box: Aabb): Vec2 => {
  const nx = Math.max(box.min.x, Math.min(c.x, box.max.x));
  const ny = Math.max(box.min.y, Math.min(c.y, box.max.y));
  const dx = c.x - nx;
  const dy = c.y - ny;
  const d2 = dx * dx + dy * dy;
  if (d2 > r * r) return c; // no overlap
  if (d2 > 1e-12) {
    const d = Math.sqrt(d2);
    const push = r - d;
    return { x: c.x + (dx / d) * push, y: c.y + (dy / d) * push };
  }
  // Center is inside the box: eject along the nearest face.
  const left = c.x - box.min.x;
  const right = box.max.x - c.x;
  const bottom = c.y - box.min.y;
  const top = box.max.y - c.y;
  const m = Math.min(left, right, bottom, top);
  if (m === left) return { x: box.min.x - r, y: c.y };
  if (m === right) return { x: box.max.x + r, y: c.y };
  if (m === bottom) return { x: c.x, y: box.min.y - r };
  return { x: c.x, y: box.max.y + r };
};

/** Segment (a→b) vs AABB, via Liang-Barsky slab clipping. True if they touch. */
export const segmentVsAabb = (a: Vec2, b: Vec2, box: Aabb): boolean => {
  let t0 = 0;
  let t1 = 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0; // parallel to this slab
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  if (!clip(-dx, a.x - box.min.x)) return false;
  if (!clip(dx, box.max.x - a.x)) return false;
  if (!clip(-dy, a.y - box.min.y)) return false;
  if (!clip(dy, box.max.y - a.y)) return false;
  return t0 <= t1;
};

/** Segment (a→b) vs circle: true if the closest point on the segment is within r. */
export const segmentVsCircle = (a: Vec2, b: Vec2, c: Vec2, r: number): boolean => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 > 0 ? ((c.x - a.x) * abx + (c.y - a.y) * aby) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + abx * t;
  const py = a.y + aby * t;
  const dx = c.x - px;
  const dy = c.y - py;
  return dx * dx + dy * dy <= r * r;
};
